/**
 * Regex pattern to match placeholders in the format {{columnKey}}
 * Handles leading/trailing spaces within the braces
 * Supports keys with hyphens (e.g., {{fc_b389e2ab-f5dc-47c1-aeb5-7ee7b7019f28}})
 */
const PLACEHOLDER_REGEX = /\{\{\s*([\w-]+)\s*\}\}/g;

/**
 * Extracts unique column keys from placeholders in the text
 * @param text - The text containing placeholders like {{columnKey}}
 * @returns Array of unique column keys found in the text
 */
export function extractPlaceholderKeys(text: string): string[] {
  const keys: string[] = [];
  let match;

  while ((match = PLACEHOLDER_REGEX.exec(text)) !== null) {
    const key = match[1];
    if (!keys.includes(key)) {
      keys.push(key);
    }
  }

  // Reset regex state
  PLACEHOLDER_REGEX.lastIndex = 0;

  return keys;
}

/**
 * Replaces placeholders in the text with their corresponding values
 * @param text - The text containing placeholders like {{columnKey}}
 * @param values - Object mapping column keys to their formatted values
 * @returns The text with placeholders replaced by values
 */
export function replacePlaceholders(
  text: string,
  values: Record<string, string | undefined>
): string {
  return text.replace(PLACEHOLDER_REGEX, (match, key: string) => {
    const value = values[key];
    // Return original placeholder if value is not found (undefined)
    // Otherwise return the formatted value (null values are already formatted to '--' by emptyValueFormatter)
    return value !== undefined ? value : match;
  });
}

/**
 * Checks if the text contains any placeholders
 * @param text - The text to check for placeholders
 * @returns True if the text contains at least one placeholder
 */
export function hasPlaceholders(text: string): boolean {
  PLACEHOLDER_REGEX.lastIndex = 0;
  const result = PLACEHOLDER_REGEX.test(text);
  PLACEHOLDER_REGEX.lastIndex = 0;
  return result;
}

/**
 * Checks if all placeholders in the text can be resolved with the provided values
 * @param text - The text containing placeholders
 * @param values - Object mapping column keys to their formatted values
 * @returns True if all placeholders have non-undefined values
 */
export function canResolveAllPlaceholders(
  text: string,
  values: Record<string, string | undefined>
): boolean {
  const keys = extractPlaceholderKeys(text);
  return keys.every((key) => values[key] !== undefined);
}
